/**
 * A project's DEFAULT SCOPE, shown as "External view" — which subtrees an
 * outside reader gets once something else has let them in. It was labelled
 * "Default access", which read as a grant and clashed with the Joveo role.
 *
 * This used to be a separate page (/studio/settings/default-config) that
 * listed only Public projects, because the write guard allowed a default rule
 * on that audience alone. It is per-project now, and legal on every external
 * audience, because build_access_filter reads these rules for every external
 * principal on all three — through a partner's mode='default' and
 * 'default_plus' branches and through the partnerless fallback.
 *
 * It is a scope, never a grant. A default rule admits nobody:
 * _RULE_GRANT_SQL has no owner_type='default' leg. Who gets in is the
 * audience (General tab), a partner grant, or a named person.
 */
import { useCallback, useEffect, useState } from "react";
import { Loader2, ShieldCheck, Info } from "lucide-react";
import { authFetch } from "../utils/authFetch";
import TreeAccessPicker from "./TreeAccessPicker";
import type { AccessRule } from "../hooks/useDocAccess";
import { audienceOf, audienceLabel, type ProjectAudience } from "./audience";

type Project = ProjectAudience & { id: string; slug: string; isPublished?: boolean };

export default function ProjectDefaultAccessTab({ project, isAdmin }: {
  project: Project;
  isAdmin: boolean;
}) {
  const audience = audienceOf(project);
  const [rules, setRules] = useState<AccessRule[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  const [loadFailed, setLoadFailed] = useState(false);

  const load = useCallback(async () => {
    // A failed GET must not read as "no default scope". It used to fall
    // through with rules=[] and loaded=true, so the amber empty-state banner
    // asserted as fact that readers see everything — and Save would then
    // write that empty list, clearing a scope nobody ever saw.
    const r = await authFetch(`/api/admin/doc-access/default/${project.id}`);
    if (!r.ok) { setLoadFailed(true); setLoaded(true); return; }
    const rows = await r.json();
    setLoadFailed(false);
    setRules(rows.map((x: { slug: string; includeChildren: boolean }) => ({
      slug: x.slug, includeChildren: x.includeChildren,
    })));
    setLoaded(true);
  }, [project.id]);

  useEffect(() => {
    if (audience === "private") { setLoaded(true); return; }
    setLoaded(false);
    load();
  }, [load, audience]);

  // A private project is never read from outside Joveo, so a default scope
  // would apply to nobody — the server refuses it. Its equivalent is the
  // Joveo role on the General tab, so point there rather than render an
  // inert picker.
  if (audience === "private") {
    return (
      <div className="px-5 py-8 text-center">
        <ShieldCheck className="size-5 text-slate-300 mx-auto mb-2" />
        <p className="text-[13px] text-slate-600 font-medium">
          Nothing outside Joveo reads a private project
        </p>
        <p className="text-[11px] text-slate-400 mt-1 max-w-sm mx-auto">
          An External view here would apply to nobody. What every Joveo person
          gets is the <span className="font-medium">Joveo access</span> setting
          on the General tab.
        </p>
      </div>
    );
  }

  const save = async () => {
    setSaving(true); setError(""); setSaved(false);
    const r = await authFetch(`/api/admin/doc-access/default/${project.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rules: rules.map(x => ({ slug: x.slug, include_children: x.includeChildren })) }),
    });
    setSaving(false);
    if (r.ok) { setSaved(true); setTimeout(() => setSaved(false), 2500); return; }
    const d = await r.json().catch(() => ({}));
    setError(typeof d.detail === "string" ? d.detail : (d.detail?.error || "Could not save"));
  };

  const WHO: Record<string, string> = {
    shared: "the external people and partners you grant",
    partner: `${project.partner?.name || "that partner"}'s people`,
    public: "every signed-in external reader",
  };

  return (
    <div className="space-y-4 max-w-2xl">
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <ShieldCheck className="size-4 text-slate-400 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <div className="text-[13px] font-medium text-slate-800">External view</div>
            <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">
              Which sections people outside Joveo can read — here,{" "}
              <span className="font-medium">{WHO[audience]}</span> on this{" "}
              {audienceLabel(project)} project, unless a partner or a person
              has their own. Leave it empty and they get the whole project.
            </p>
            <p className="text-[11px] text-slate-500 mt-1.5 leading-snug">
              It narrows what they see, never who gets in: nobody reads this
              project because of it. That takes a partner grant, a named
              share, or a Public audience.
            </p>
            <p className="text-[11px] text-slate-400 mt-1.5 leading-snug">
              It applies to read-only integrations too — the Support Portal
              token has no rules of its own, so it reads whatever this says.
              A partner set to <span className="font-medium">External view +
              extras</span> gets this <em>plus</em> their own, so widening
              here widens them.
            </p>
          </div>
        </div>
      </div>

      {/* Empty is not "deny all" — the server treats an empty list as a clear,
          and the read path treats "no default rules" as unfiltered. Say so,
          because the opposite is the natural guess. */}
      {loadFailed && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[11px] text-rose-700">
          Could not read this project's External view. Nothing below is
          trustworthy and saving is disabled — reload to try again.
        </div>
      )}

      {loaded && !loadFailed && rules.length === 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
          <Info className="size-3.5 text-amber-600 mt-0.5 shrink-0" />
          <p className="text-[11px] text-amber-800 leading-snug">
            Nothing selected, so nothing is narrowed: readers who reach
            this project see all of it. Tick the sections they should get.
          </p>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[11px] text-rose-700">
          {error}
        </div>
      )}

      {!loaded || loadFailed ? (
        loadFailed ? null : (
          <div className="py-10 text-center text-sm text-slate-400">
            <Loader2 className="size-4 animate-spin inline mr-2" />Loading…
          </div>
        )
      ) : (
        <div className={isAdmin ? "" : "pointer-events-none opacity-60"}>
          <TreeAccessPicker projectSlug={project.slug} rules={rules} onChange={setRules} />
        </div>
      )}

      {isAdmin && loaded && !loadFailed && (
        <div className="flex items-center gap-3">
          <button onClick={save} disabled={saving}
            className="px-4 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors">
            {saving ? "Saving…" : "Save External view"}
          </button>
          {saved && <span className="text-[11px] text-emerald-600 font-medium">Saved</span>}
          <span className="ml-auto text-[11px] text-slate-400">
            {rules.length} section{rules.length === 1 ? "" : "s"} selected
          </span>
        </div>
      )}
    </div>
  );
}
